// noinspection GraphQLUnresolvedReference

import React, {useCallback, useMemo, useRef, useState} from "react";
import {MaterialReactTable, type MRT_ColumnDef, type MRT_Row, MRT_TableInstance} from "material-react-table";
import {
	Box,
	Button,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	MenuItem,
	Radio,
	RadioGroup,
	TextField,
	Tooltip,
	Typography
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {apiRequest} from "../../services/utils/HttpUtils";
import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query";
import {CreationMode, Scenario} from "./TableData";
import {useMsal} from "@azure/msal-react";
import {scenarioDescriptionIntro} from "../../assets/texts/pageTextContent";
import {
	SCENARIO_QUERY,
	CREATE_SCENARIO,
	DELETE_SCENARIO,
	CREATE_BASE_ON_SCENARIO
} from "../../graphql/queries_scenarioEditor"
import {getUserInfo} from "../../graphql/queries_user_info";

type Props = {
    setTabIndex: React.Dispatch<React.SetStateAction<number>>;
    setScenarioID: React.Dispatch<React.SetStateAction<string>>;
};

export default function ScenarioEditor(props: Props) {
	const [baseScenario, setBaseScenario] = React.useState("1");
	const [scenarioName, setScenarioName] = React.useState("");
	const [scenarioDesc, setScenarioDesc] = React.useState("");
	const [creationMode, setCreationMode] = React.useState<CreationMode>(CreationMode.New);
	const {setTabIndex, setScenarioID} = props;
	const tableInstanceRef = useRef<MRT_TableInstance<Scenario>>(null);
	const client =  useQueryClient();
	const { instance, accounts} = useMsal();
  const {data: userInfo} = getUserInfo();
  console.log("userInfo", userInfo)
	const [rowSelection, setRowSelection] = useState({});

	const {data, isLoading, isFetching} = useQuery({
		queryKey: ["scenarioList"],
		queryFn: () => {
			return apiRequest(
				{
					query: SCENARIO_QUERY,
					variables: {inOwner: accounts[0].username},
					instance: instance,
					accounts: accounts
				})
		},
		cacheTime: Infinity}
	);
	const {mutate: createScenario} = useMutation(["createScenario"], {
		mutationFn: (variables: any) => {
			if (variables["base"] == "1") {
				return apiRequest(
					{
						query: CREATE_SCENARIO,
						variables: variables,
						instance: instance,
						accounts: accounts
					})
			} else {
				return apiRequest(
					{
						query: CREATE_BASE_ON_SCENARIO,
						variables: variables,
						instance: instance,
						accounts: accounts
					})
			}

		},
		onSuccess: (data) => {
			client.invalidateQueries(["scenarioList"]);
			// @ts-ignore
			setScenarioID(data.executeCreateScenario?
				// @ts-ignore
				data.executeCreateScenario[0].ScenarioID:
				// @ts-ignore
				data.executeCreateBaseOnScenario[0].ScenarioID );
			client.invalidateQueries(["projectList"]);
			setTabIndex(1);
		},
	});
	const deleteScenario = useMutation({
		mutationFn: (inScenarioID: string) => {
			return apiRequest(
				{
					query: DELETE_SCENARIO,
					variables: {inScenarioID: inScenarioID},
					instance: instance,
					accounts: accounts
				})
		},
		onSuccess: () => {
			client.invalidateQueries(["scenarioList"]);
		},
	});

	const handleBaseModelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setBaseScenario(event.target.value as string);
	};

	const handleCreationModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		// @ts-ignore
		setCreationMode(event.target.value as string);
	};
	const handleDeleteRow = useCallback(
		(row: MRT_Row<Scenario>) => {
			if (
				!confirm(`Are you sure you want to delete ${row.getValue("ScenarioTitle")}`)
			) {
				return;
			}
			//send api delete request here, then refetch or update local table data for re-render
			deleteScenario.mutate(row.getValue("ScenarioID"));
		},
		[deleteScenario],
	);

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		// @ts-ignore
		if (!data?.executeGetScenarioList?.map((item) => item.ScenarioTitle).includes(scenarioName)) {
			createScenario({
				owner: accounts[0].username,
				title: scenarioName,
				desc: scenarioDesc,
				base: baseScenario,
				// @ts-ignore
				juri: userInfo?.executeGetUser[0]?.Jurisdiction ?? ""
			});
		} else {
			alert("Scenario Name need to be unique");
		}
	};
	const columns = useMemo<MRT_ColumnDef<Scenario>[]>(
		() => [
			{
				accessorKey: "ScenarioID",
				header: "Scenario ID"
			},
			{
				accessorKey: "ScenarioTitle",
				header: "Scenario name",
				minSize: 500,
				maxSize: 600
			},
			{
				accessorFn: (row) => new Date(row.DateCreated),
				header: "Creation Date",
				Cell: ({cell}) => cell.getValue<Date>()?.toLocaleDateString(),
				maxSize: 50
			},
			{
				accessorKey: "ScenarioDescription",
				header: "Description"
			},
		], []);
	return <Box sx={{zIndex: "1000", position: "relative"}}>
		<form onSubmit={handleSubmit}>
			<Grid
				container
				justifyContent='space-between'
				sx={{height: "100%"}}
				className='CreateScenarioGrid'
				spacing={2}
			>
				<Grid item xs={3}>
					<Box sx={{backgroundColor: "#E0E7EC", p: 2}}>
						<Grid
							container
							spacing={2}
							direction='column'
							justifyContent='space-around'
						>
							<Grid item xs={2}>
								<Typography variant={"h3"}>Create a scenario</Typography>
							</Grid>
							<Grid item xs={2}>
								<FormControl>
									<RadioGroup
										row
										defaultValue="new"
										name="radio-buttons-group"
										onChange={handleCreationModeChange}
									>
										<FormControlLabel value={CreationMode.New} control={<Radio/>} label="New"/>
										<FormControlLabel value={CreationMode.Saved} control={<Radio/>}
											label="Based on a saved scenario"/>
									</RadioGroup>
								</FormControl>
							</Grid>
							<Grid item xs={2}>
								{creationMode == CreationMode.Saved && <TextField
									value={baseScenario}
									onChange={handleBaseModelChange}
									label={"Choose saved scenario"}
									sx={{width: "100%", backgroundColor: "#ffffff"}}
									select
									required
									helperText="Scenario In Current Baseline"
								>
									{
										// @ts-ignore
										data?.executeGetScenarioList?.filter((i) => i.ScenarioEditable > 0).map((item) => (
											<MenuItem
												key={item.ScenarioID}
												value={item.ScenarioID}
											>{item.ScenarioTitle}</MenuItem>
										))}
									<MenuItem value={1}>Baseline Plan</MenuItem>
								</TextField>}
							</Grid>
							<Grid item xs={2}>
								<TextField
									sx={{
										width: "100%",
										backgroundColor: "#ffffff"
									}}
									error={scenarioName.length >= 40}
									helperText={scenarioName.length >= 40 ?
										"Scenario names should be 40 characters or less" :
										""
									}
									label={"Name your new scenario"}
									value={scenarioName}
									onChange={(event) =>
										setScenarioName(event.target.value)
									}
									inputProps={{ maxLength: 40 }}
									required
								/>
							</Grid>
							<Grid item xs={2}>
								<TextField
									sx={{width: "100%", backgroundColor: "#ffffff"}}
									multiline
									value={scenarioDesc}
									rows={5}
									placeholder={scenarioDescriptionIntro}
									onChange={(event) =>
										setScenarioDesc(event.target.value)
									}
								/>
							</Grid>
							<Grid item xs={2} sx={{
								display: "flex",
								justifyContent: "flex-end"
							}}>
								<Button
									variant="contained"
									type="submit"
								>
                                    Create</Button>
							</Grid>
						</Grid>
					</Box>
				</Grid>
				<Grid item xs={9}>
					<Box
						sx={{p: 2, backgroundColor: "#E0E7EC", height: "1"}}>
						<Grid
							container
							spacing={2}
							direction='column'>
							<Grid item xs={1}>
								<Typography variant={"h3"}>Saved scenarios</Typography>
							</Grid>
							<Grid item xs={10}>
								<MaterialReactTable
									columns={columns}
									data={
										// @ts-ignore
										data?.executeGetScenarioList ?? []}
									enableMultiRowSelection={false} //shows radio buttons instead of checkboxes
									enableRowSelection
									onRowSelectionChange={setRowSelection}
									enableRowActions
									muiTableContainerProps={{sx: {maxHeight: "40vh"}}}
									positionActionsColumn='last'
									renderRowActions={({row}) => (
										<Box sx={{display: "flex", gap: "1rem"}}>
											<Tooltip arrow title={row.original.ScenarioDescription}>
												<IconButton>
													<InfoOutlinedIcon/>
												</IconButton>
											</Tooltip>
											<Tooltip arrow placement="right" title="Delete">
												<IconButton color="error" onClick={() => handleDeleteRow(row)}>
													<DeleteOutlineOutlinedIcon/>
												</IconButton>
											</Tooltip>
										</Box>
									)}
									displayColumnDefOptions={{
										"mrt-row-actions": {
											size: 5, //make actions column smaller
										},
									}}
									enableColumnFilters={false}
									enablePagination={false}
									enableBottomToolbar={false}
									enableTopToolbar={false}
									enableTableHead={false}
									initialState={{
										density: "compact",
										columnVisibility: {
											ScenarioID: false,
											ScenarioDescription: false,
										}
									}}
									state={{
										isLoading,
										showProgressBars: isFetching,
										rowSelection
									}}
									tableInstanceRef={tableInstanceRef}
								/>
							</Grid>
							<Grid item xs={1} sx={{
								display: "flex",
								justifyContent: "flex-end",
							}}>
								<Button
									variant="contained"
									onClick={() => {
										setTabIndex(1);
										// @ts-ignore
										setScenarioID(tableInstanceRef?.current?.getSelectedRowModel().flatRows[0].original.ScenarioID);
									}}
									disabled={Object.keys(rowSelection).length === 0}
								>
                                    Open
								</Button>
							</Grid>
						</Grid>
					</Box>
				</Grid>
			</Grid>
		</form>
	</Box>;
}
